/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class PredictiveSearch extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form.search-form')
    this.input = this.form.querySelector('input[type="search"]')
    this.results = this.form.querySelector('#predictive-search')
    this.status = this.form.querySelector('#predictive-search-status')

    this.emptyContainer = this.querySelector('.search-empty')
    this.cartUpsells = this.querySelector('.cart-upsells')
    this.searchRequestId = 0
    this.abortController = null

    const searchAfterTyping = window.theme.debounce(() => this.getSearchResults(), 300)
    this.input.addEventListener('input', () => {
      // Invalidate immediately, including while the next search is debouncing.
      this.cancelSearchRequest()
      this.clearResults()
      if (this.input.value.trim()) searchAfterTyping()
    })

    document.querySelector('#offcanvas-search')?.addEventListener('shown.bs.offcanvas', () => {
      this.input.focus()
    })
    this.accessibilityEventIsInit = false
    this.handleAccessibility()
  }

  cancelSearchRequest () {
    this.searchRequestId++
    this.abortController?.abort()
    this.abortController = null
  }

  clearResults () {
    this.results.innerHTML = ''
    this.results.removeAttribute('aria-busy')
    this.emptyContainer?.removeAttribute('hidden')
    this.cartUpsells?.removeAttribute('hidden')
    this.input.setAttribute('aria-expanded', 'false')
    this.input.removeAttribute('aria-activedescendant')
    if (this.status) this.status.textContent = ''
  }

  async getSearchResults () {
    const searchTerm = this.input.value.trim()
    this.cancelSearchRequest()
    this.clearResults()
    if (!searchTerm) return

    const requestId = this.searchRequestId
    this.abortController = new AbortController()
    this.results.setAttribute('aria-busy', 'true')
    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': this.dataset.resourcesType,
      'resources[limit]': '10',
      section_id: 'predictive-search'
    })

    try {
      const response = await fetch(`${window.Shopify.routes.predictive_search_url}?${params}`, {
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error('Predictive search unavailable')

      const data = await response.text()
      if (requestId !== this.searchRequestId || this.input.value.trim() !== searchTerm) return

      const section = new DOMParser().parseFromString(data, 'text/html').querySelector('#shopify-section-predictive-search')
      if (!section) throw new Error('Predictive search response is missing its section')

      this.results.innerHTML = section.innerHTML
      this.emptyContainer?.setAttribute('hidden', 'hidden')
      this.cartUpsells?.setAttribute('hidden', 'hidden')
      this.input.setAttribute('aria-expanded', 'true')
      if (this.status) {
        this.status.textContent = this.results.querySelector('#predictive-search-results')?.dataset.resultsText || ''
      }
      this.results.querySelectorAll('.jdgm-prev-badge__stars').forEach(elem => {
        elem.setAttribute('tabindex', '-1')
      })
    } catch (error) {
      if (requestId !== this.searchRequestId || error.name === 'AbortError') return
      // The regular search form remains available if suggestions cannot load.
      this.clearResults()
    } finally {
      if (requestId === this.searchRequestId) {
        this.abortController = null
        this.results.removeAttribute('aria-busy')
      }
    }
  }

  handleAccessibility () {
    if (this.accessibilityEventIsInit) return
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
      const items = Array.from(this.results.querySelectorAll('#predictive-search-results a, #predictive-search-results button'))
      if (!items.length) return

      event.preventDefault()
      const currentIndex = items.indexOf(document.activeElement)
      const nextIndex = event.key === 'ArrowDown'
        ? (currentIndex + 1) % items.length
        : (currentIndex <= 0 ? items.length : currentIndex) - 1

      items.forEach(elem => {
        elem.closest('[role="option"]')?.setAttribute('aria-selected', 'false')
      })
      const nextItem = items[nextIndex]
      const option = nextItem.closest('[role="option"]')
      nextItem.focus()
      option?.setAttribute('aria-selected', 'true')
      if (option?.id) {
        this.input.setAttribute('aria-activedescendant', option.id)
      } else {
        this.input.removeAttribute('aria-activedescendant')
      }
    })
    this.accessibilityEventIsInit = true
  }
}

customElements.define('predictive-search', PredictiveSearch)
